/**
 * DataTable — reusable table with:
 *   • Column resizing (drag handle)
 *   • Column visibility (show/hide via "+ Add column" panel)
 *   • Sorting (click column header menu → Asc / Desc)
 *   • Column drag-reorder (@dnd-kit)
 *   • Row drag-reorder (@dnd-kit) — enabled via rowDragEnabled + onRowReorder
 *   • Preferences persisted to localStorage
 */
import {
  type CSSProperties,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import {
  closestCenter,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import type { DragEndEvent, DragOverEvent, DragStartEvent } from "@dnd-kit/core"
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import type {
  ColumnDef,
  ColumnOrderState,
  ColumnSizingState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  ViewOffIcon,
  Add01Icon,
  Search01Icon,
  Tick01Icon,
  DragDropVerticalIcon,
} from "@hugeicons/core-free-icons"
import { cn } from "@/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import type { ColumnMeta, TablePrefs } from "./use-table-prefs"
import { useTablePrefs } from "./use-table-prefs"

// ─── Constants ────────────────────────────────────────────────────────────────

const ROW_ID_PREFIX = "row:"
const INTERACTIVE_SELECTOR = [
  "a",
  "button",
  "input",
  "textarea",
  "select",
  "[contenteditable='true']",
  "[role='button']",
  "[role='menuitem']",
  "[data-no-row-drag]",
].join(",")

// ─── Extended column def ──────────────────────────────────────────────────────

export type DataTableColumnDef<TData> = ColumnDef<TData> & {
  /** Must match a ColumnMeta id */
  id: string
  /** Used for "Add column" panel and column header label */
  meta?: {
    label: string
    pinned?: boolean
    defaultVisible?: boolean
    icon?: any
  }
}

// ─── Group support ────────────────────────────────────────────────────────────

export type DataTableGroup<TData> = {
  /** Stable unique key for this group */
  key: string
  /** Content rendered in the group header row (spanning full width) */
  header: ReactNode
  /** Rows belonging to this group */
  rows: TData[]
}

// ─── Props ────────────────────────────────────────────────────────────────────

export type DataTableProps<TData> = {
  tableKey: string
  data: TData[]
  columns: DataTableColumnDef<TData>[]
  /** IDs of columns that are always visible and cannot be hidden or moved before (pinned left) */
  pinnedColumnIds?: string[]
  /** Minimum trailing width for "Add column" button area */
  trailingMinWidth?: number
  /** Footer slot: e.g. row count */
  footer?: ReactNode
  /** Called when user clicks "+ Create new column" (custom attributes) */
  onCreateColumn?: () => void
  className?: string
  /**
   * When provided, the table renders group section headers between groups.
   * Sorting is disabled in grouped mode (data order is preserved).
   * When `groups` is set, the `data` prop is ignored.
   */
  groups?: DataTableGroup<TData>[]

  // ── Row drag-and-drop ──
  /** Enable row drag handles. Requires getRowId. */
  rowDragEnabled?: boolean
  /** Extract a stable string ID from a row. Required when rowDragEnabled. */
  getRowId?: (row: TData) => string
  /**
   * Called when the user drops a row into a new position.
   * Receives the full new ordered array of IDs.
   */
  onRowReorder?: (newOrderIds: string[]) => void
  prefs?: TablePrefs
  onPrefsChange?: Dispatch<SetStateAction<TablePrefs>>
}

// ─── Column meta ──────────────────────────────────────────────────────────────

export function buildDataTableColumnMeta(columns: DataTableColumnDef<unknown>[], pinnedIds: Set<string>): ColumnMeta[] {
  return columns
    .filter((col) => col.enableHiding !== false)
    .map((col) => ({
      id: col.id,
      label: col.meta?.label ?? col.id,
      defaultWidth: typeof col.size === "number" ? col.size : 160,
      defaultVisible: col.meta?.defaultVisible ?? true,
      pinned: pinnedIds.has(col.id),
      icon: col.meta?.icon,
    }))
}

function getFixedColumnIds(columns: DataTableColumnDef<unknown>[]): Set<string> {
  return new Set(columns.filter((col) => col.enableHiding === false).map((col) => col.id))
}

// ─── Sortable header cell ─────────────────────────────────────────────────────

function SortableHeaderCell({
  headerId,
  children,
  style,
}: {
  headerId: string
  children: ReactNode
  style: CSSProperties
}) {
  const sortable = useSortable({ id: headerId })
  return (
    <th
      ref={sortable.setNodeRef}
      style={{
        ...style,
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.7 : undefined,
      }}
      {...sortable.attributes}
      className="relative h-8 border-b border-border/60 bg-background p-0 text-left select-none"
    >
      <div className="flex items-stretch">
        <button
          type="button"
          className="flex cursor-grab items-center justify-center px-1.5 text-muted-foreground/40 hover:text-muted-foreground active:cursor-grabbing"
          {...sortable.listeners}
          aria-label="Перетащить колонку"
          tabIndex={-1}
        >
          <HugeiconsIcon icon={DragDropVerticalIcon} strokeWidth={2} className="size-3" />
        </button>
        {children}
      </div>
    </th>
  )
}

// ─── Sortable row ─────────────────────────────────────────────────────────────

function SortableRow({
  id,
  children,
  isDraggingThis,
}: {
  id: string
  children: ReactNode
  isDraggingThis: boolean
}) {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({ id: `${ROW_ID_PREFIX}${id}` })

  function handleDragStartCapture(event: React.PointerEvent<HTMLTableRowElement>) {
    const target = event.target as HTMLElement | null
    const interactive = target?.closest(INTERACTIVE_SELECTOR)
    if (interactive && interactive !== event.currentTarget) {
      event.stopPropagation()
    }
  }

  return (
    <tr
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onPointerDownCapture={handleDragStartCapture}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        "group cursor-grab touch-none select-none transition-colors hover:bg-muted/30 active:cursor-grabbing",
        isDragging && "cursor-grabbing",
        isDraggingThis && "opacity-30",
      )}
    >
      {children}
    </tr>
  )
}

// ─── Header cell inner ────────────────────────────────────────────────────────

function HeaderCellInner({
  label,
  sortDirection,
  onSortAsc,
  onSortDesc,
  canMoveLeft,
  canMoveRight,
  canHide,
  onMoveLeft,
  onMoveRight,
  onHide,
}: {
  label: string
  sortDirection: "asc" | "desc" | false
  onSortAsc: () => void
  onSortDesc: () => void
  canMoveLeft: boolean
  canMoveRight: boolean
  canHide: boolean
  onMoveLeft: () => void
  onMoveRight: () => void
  onHide: () => void
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-full flex-1 items-center gap-1.5 px-3 text-ui-value font-normal text-muted-foreground hover:text-foreground"
        >
          <span className="truncate">{label}</span>
          {sortDirection === "asc" && (
            <HugeiconsIcon icon={ArrowUp01Icon} strokeWidth={2} className="size-3 shrink-0 text-foreground" />
          )}
          {sortDirection === "desc" && (
            <HugeiconsIcon icon={ArrowDown01Icon} strokeWidth={2} className="size-3 shrink-0 text-foreground" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        <DropdownMenuItem onClick={onSortAsc} className="gap-2 text-xs">
          {sortDirection === "asc" ? (
            <HugeiconsIcon icon={Tick01Icon} strokeWidth={2} className="size-3.5" />
          ) : (
            <HugeiconsIcon icon={ArrowUp01Icon} strokeWidth={2} className="size-3.5" />
          )}
          Сортировать по возрастанию
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onSortDesc} className="gap-2 text-xs">
          {sortDirection === "desc" ? (
            <HugeiconsIcon icon={Tick01Icon} strokeWidth={2} className="size-3.5" />
          ) : (
            <HugeiconsIcon icon={ArrowDown01Icon} strokeWidth={2} className="size-3.5" />
          )}
          Сортировать по убыванию
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onMoveLeft} disabled={!canMoveLeft} className="gap-2 text-xs">
          <HugeiconsIcon icon={ArrowLeft01Icon} strokeWidth={2} className="size-3.5" />
          Переместить влево
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onMoveRight} disabled={!canMoveRight} className="gap-2 text-xs">
          <HugeiconsIcon icon={ArrowRight01Icon} strokeWidth={2} className="size-3.5" />
          Переместить вправо
        </DropdownMenuItem>
        {canHide && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onHide} className="gap-2 text-xs">
              <HugeiconsIcon icon={ViewOffIcon} strokeWidth={2} className="size-3.5" />
              Скрыть колонку
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ─── Pinned (name) header ─────────────────────────────────────────────────────

function PinnedHeaderCell({
  children,
  style,
}: {
  children: ReactNode
  style: CSSProperties
}) {
  return (
    <th
      style={style}
      className="sticky left-0 z-10 h-8 border-b border-border/60 bg-background p-0 text-left select-none"
    >
      {children}
    </th>
  )
}

// ─── Resize handle ────────────────────────────────────────────────────────────

function ResizeHandle({
  onMouseDown,
  onTouchStart,
  isResizing,
}: {
  onMouseDown: (e: React.MouseEvent) => void
  onTouchStart: (e: React.TouchEvent) => void
  isResizing: boolean
}) {
  return (
    <div
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      className={cn(
        "absolute top-0 right-0 h-full w-1 cursor-col-resize select-none touch-none",
        "after:absolute after:inset-y-2 after:right-0 after:w-px after:bg-border/60 after:transition-colors",
        "hover:after:bg-border",
        isResizing && "after:bg-primary",
      )}
    />
  )
}

// ─── Add column panel ─────────────────────────────────────────────────────────

function AddColumnPanel({
  hiddenColumns,
  onShow,
  onClose,
  onCreateColumn,
}: {
  hiddenColumns: { id: string; label: string; icon?: any }[]
  onShow: (id: string) => void
  onClose: () => void
  onCreateColumn?: () => void
}) {
  const [search, setSearch] = useState("")
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [onClose])

  const filtered = hiddenColumns.filter((col) =>
    col.label.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    <div
      ref={panelRef}
      className="absolute top-full right-3 z-50 mt-1 w-64 rounded-lg border bg-popover p-1 shadow-lg"
    >
      <div className="relative px-1 pb-1">
        <HugeiconsIcon
          icon={Search01Icon}
          strokeWidth={2}
          className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск колонок..."
          className="h-7 pl-8 text-ui-value font-normal placeholder:font-normal"
          autoFocus
        />
      </div>
      <div className="max-h-56 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="px-3 py-2 text-ui-value font-normal text-muted-foreground">
            {hiddenColumns.length === 0 ? "Все колонки уже отображаются" : "Ничего не найдено"}
          </p>
        ) : (
          filtered.map((col) => (
            <button
              key={col.id}
              type="button"
              className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-ui-value font-normal hover:bg-muted"
              onClick={() => onShow(col.id)}
            >
              {col.icon && (
                <HugeiconsIcon icon={col.icon} strokeWidth={2} className="size-4 text-muted-foreground shrink-0" />
              )}
              <span className="flex-1 text-left">{col.label}</span>
            </button>
          ))
        )}
      </div>
      {onCreateColumn && (
        <>
          <div className="my-1 border-t" />
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-ui-value font-normal hover:bg-muted"
            onClick={() => {
              onCreateColumn()
              onClose()
            }}
          >
            <HugeiconsIcon icon={Add01Icon} strokeWidth={2} className="size-3.5" />
            Создать новую колонку
          </button>
        </>
      )}
    </div>
  )
}

// ─── DataTable ────────────────────────────────────────────────────────────────

export function DataTable<TData>({
  tableKey,
  data,
  columns,
  pinnedColumnIds = [],
  trailingMinWidth = 140,
  footer,
  onCreateColumn,
  className,
  groups,
  rowDragEnabled = false,
  getRowId,
  onRowReorder,
  prefs: controlledPrefs,
  onPrefsChange,
}: DataTableProps<TData>) {
  const pinnedSet = new Set(pinnedColumnIds)
  const fixedSet = getFixedColumnIds(columns as DataTableColumnDef<unknown>[])

  const effectiveData = useMemo(
    () => (groups ? groups.flatMap((g) => g.rows) : data),
    [groups, data],
  )

  // ── Row drag state ──
  const rowIdOf = useMemo(
    () => getRowId ?? ((row: TData) => (row as any).id ?? ""),
    [getRowId],
  )

  // Local row ID order — synced from props when not mid-drag
  const [dragRowIds, setDragRowIds] = useState<string[]>(() => effectiveData.map(rowIdOf))
  const [activeRowId, setActiveRowId] = useState<string | null>(null)
  const dragRowIdsRef = useRef(dragRowIds)

  useEffect(() => {
    dragRowIdsRef.current = dragRowIds
  }, [dragRowIds])

  // Keep local order in sync with external data changes (but not while dragging)
  useEffect(() => {
    if (activeRowId) return
    setDragRowIds(effectiveData.map(rowIdOf))
  }, [effectiveData, rowIdOf, activeRowId])

  // Build displayData in local drag order (for TanStack to render)
  const rowById = useMemo(() => {
    const map = new Map<string, TData>()
    for (const row of effectiveData) map.set(rowIdOf(row), row)
    return map
  }, [effectiveData, rowIdOf])

  const displayData = useMemo(() => {
    if (!rowDragEnabled || dragRowIds.length === 0) return effectiveData
    return dragRowIds.map((id) => rowById.get(id)).filter((r): r is TData => r !== undefined)
  }, [rowDragEnabled, dragRowIds, rowById, effectiveData])

  // Build column meta only for manageable columns
  const columnMeta = buildDataTableColumnMeta(columns as DataTableColumnDef<unknown>[], pinnedSet)
  const [internalPrefs, setInternalPrefs] = useTablePrefs(tableKey, columnMeta)
  const prefs = controlledPrefs ?? internalPrefs
  const setPrefs = onPrefsChange ?? setInternalPrefs

  const [addOpen, setAddOpen] = useState(false)
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false)
  const [containerWidth, setContainerWidth] = useState(0)
  const tableWrapRef = useRef<HTMLDivElement>(null)
  const addBtnRef = useRef<HTMLButtonElement>(null)

  // ── TanStack table ──
  const sorting: SortingState = prefs.sorting

  const table = useReactTable({
    data: displayData,
    columns: columns as ColumnDef<TData>[],
    getRowId: rowDragEnabled ? (row) => rowIdOf(row) : undefined,
    state: {
      columnOrder: prefs.columnOrder as ColumnOrderState,
      columnVisibility: prefs.columnVisibility as VisibilityState,
      columnSizing: prefs.columnWidths as ColumnSizingState,
      sorting,
    },
    onColumnOrderChange: (updater) => {
      setPrefs((cur) => {
        const next = typeof updater === "function" ? updater(cur.columnOrder as ColumnOrderState) : updater
        return { ...cur, columnOrder: enforcePinnedFirst(next, pinnedColumnIds) }
      })
    },
    onColumnVisibilityChange: (updater) => {
      setPrefs((cur) => {
        const next = typeof updater === "function" ? updater(cur.columnVisibility as VisibilityState) : updater
        const forced = Object.fromEntries(pinnedColumnIds.map((id) => [id, true]))
        return { ...cur, columnVisibility: { ...next, ...forced } }
      })
    },
    onColumnSizingChange: (updater) => {
      setPrefs((cur) => {
        const next = typeof updater === "function" ? updater(cur.columnWidths as ColumnSizingState) : updater
        return { ...cur, columnWidths: next }
      })
    },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater
      setPrefs((cur) => ({ ...cur, sorting: next.slice(0, 1) }))
    },
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: groups !== undefined && groups.length > 0,
  })

  const visibleLeafColumns = table.getVisibleLeafColumns()
  const pinnedWidth = visibleLeafColumns
    .filter((col) => pinnedSet.has(col.id))
    .reduce((sum, col) => sum + col.getSize(), 0)

  const totalDataWidth = table.getTotalSize()
  // Row dragging uses the whole non-interactive row surface, without a handle column.
  const dragHandleOffset = 0
  const trailingWidth = Math.max(trailingMinWidth, containerWidth - totalDataWidth - dragHandleOffset)
  const tableWidth = totalDataWidth + dragHandleOffset + trailingWidth

  // ── DnD sensors ──
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor),
  )

  // Sortable column IDs (non-pinned, non-fixed)
  const sortableColumnIds = visibleLeafColumns
    .map((c) => c.id)
    .filter((id) => !pinnedSet.has(id) && !fixedSet.has(id))

  // Sortable row IDs (prefixed)
  const sortableRowIds = useMemo(
    () => (rowDragEnabled ? dragRowIds.map((id) => `${ROW_ID_PREFIX}${id}`) : []),
    [rowDragEnabled, dragRowIds],
  )

  function handleDragStart(event: DragStartEvent) {
    const id = String(event.active.id)
    if (id.startsWith(ROW_ID_PREFIX)) {
      setActiveRowId(id.slice(ROW_ID_PREFIX.length))
    }
  }

  function handleDragOver(event: DragOverEvent) {
    const activeId = String(event.active.id)
    const overId = String(event.over?.id ?? "")
    if (!activeId.startsWith(ROW_ID_PREFIX) || !overId.startsWith(ROW_ID_PREFIX)) return

    const rawActive = activeId.slice(ROW_ID_PREFIX.length)
    const rawOver = overId.slice(ROW_ID_PREFIX.length)
    setDragRowIds((prev) => {
      const oldIndex = prev.indexOf(rawActive)
      const newIndex = prev.indexOf(rawOver)
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return prev
      const next = arrayMove(prev, oldIndex, newIndex)
      dragRowIdsRef.current = next
      return next
    })
  }

  function handleDragEnd(event: DragEndEvent) {
    const activeId = String(event.active.id)
    const overId = String(event.over?.id ?? "")

    // ── Row drag end ──
    if (activeId.startsWith(ROW_ID_PREFIX)) {
      setActiveRowId(null)
      if (event.over) {
        onRowReorder?.(dragRowIdsRef.current)
      } else {
        // Dropped outside — reset
        const next = effectiveData.map(rowIdOf)
        dragRowIdsRef.current = next
        setDragRowIds(next)
      }
      return
    }

    // ── Column drag end ──
    if (!event.over || activeId === overId) return
    setPrefs((cur) => {
      const oldIndex = cur.columnOrder.indexOf(activeId)
      const newIndex = cur.columnOrder.indexOf(overId)
      if (oldIndex < 0 || newIndex < 0) return cur
      return { ...cur, columnOrder: enforcePinnedFirst(arrayMove(cur.columnOrder, oldIndex, newIndex), pinnedColumnIds) }
    })
  }

  function moveColumn(columnId: string, direction: -1 | 1) {
    setPrefs((cur) => {
      const visibleOrder = cur.columnOrder.filter((id) => cur.columnVisibility[id] !== false)
      const index = visibleOrder.indexOf(columnId)
      const targetId = visibleOrder[index + direction]
      if (!targetId || pinnedSet.has(targetId)) return cur
      const curOrder = cur.columnOrder
      const nextOrder = arrayMove(curOrder, curOrder.indexOf(columnId), curOrder.indexOf(targetId))
      return { ...cur, columnOrder: enforcePinnedFirst(nextOrder, pinnedColumnIds) }
    })
  }

  function showColumn(columnId: string) {
    setPrefs((cur) => ({
      ...cur,
      columnVisibility: { ...cur.columnVisibility, [columnId]: true },
    }))
    setAddOpen(false)
  }

  function hideColumn(columnId: string) {
    setPrefs((cur) => ({
      ...cur,
      columnVisibility: { ...cur.columnVisibility, [columnId]: false },
    }))
  }

  const hiddenColumns = columnMeta
    .filter((col) => !col.pinned && prefs.columnVisibility[col.id] === false)
    .map((col) => ({ id: col.id, label: col.label, icon: col.icon }))

  function fixedStyle(width: number): CSSProperties {
    return { width, minWidth: width, maxWidth: width }
  }

  function handleScroll() {
    const el = tableWrapRef.current
    if (!el) return
    setHasHorizontalScroll(el.scrollLeft > 0)
  }

  useEffect(() => {
    const el = tableWrapRef.current
    if (!el) return

    const syncWidth = () => setContainerWidth(el.clientWidth)
    syncWidth()

    const observer = new ResizeObserver(() => {
      syncWidth()
    })
    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  // Ghost row data for DragOverlay
  const activeRowData = activeRowId ? rowById.get(activeRowId) : undefined
  const activeRow = activeRowData
    ? table.getRowModel().rows.find((r) => rowIdOf(r.original) === activeRowId)
    : undefined

  // ── Render helpers ──

  function renderRowCells(row: import("@tanstack/react-table").Row<TData>, forOverlay = false) {
    return row.getVisibleCells().map((cell) => {
      const isPinned = pinnedSet.has(cell.column.id)
      return (
        <td
          key={cell.id}
          style={fixedStyle(cell.column.getSize())}
          className={cn(
            "overflow-hidden px-0 py-0",
            !forOverlay && isPinned &&
              "sticky left-0 z-[1] bg-background group-hover:bg-[color-mix(in_oklch,var(--muted)_30%,var(--background))]",
            forOverlay && isPinned && "bg-background",
          )}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      )
    })
  }

  return (
    <div
      className={cn(
        "relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden",
        className,
      )}
    >
      {/* Pinned-column shadow */}
      {hasHorizontalScroll && pinnedWidth > 0 && (
        <div
          className="pointer-events-none absolute inset-y-0 z-[25] w-3 bg-gradient-to-r from-black/[0.07] to-transparent"
          style={{ left: pinnedWidth + dragHandleOffset }}
        />
      )}
      <div
        ref={tableWrapRef}
        className="min-h-0 flex-1 min-w-0 overflow-auto"
        onScroll={handleScroll}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <table
            className="border-separate border-spacing-0 text-ui-value font-normal"
            style={{ width: tableWidth, minWidth: tableWidth }}
          >
            <thead className="sticky top-0 z-20 bg-background">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  <SortableContext items={sortableColumnIds} strategy={horizontalListSortingStrategy}>
                    {headerGroup.headers.map((header) => {
                      const isPinned = pinnedSet.has(header.column.id)
                      const isFixed = fixedSet.has(header.column.id)
                      const visibleOrder = visibleLeafColumns.map((c) => c.id)
                      const visibleIndex = visibleOrder.indexOf(header.column.id)
                      const activeSortItem = prefs.sorting[0]
                      const sortDirection =
                        activeSortItem?.id === header.column.id
                          ? activeSortItem.desc
                            ? ("desc" as const)
                            : ("asc" as const)
                          : false
                      const colMeta = columnMeta.find((m) => m.id === header.column.id)
                      const label = colMeta?.label ?? header.column.id
                      const style = fixedStyle(header.getSize())

                      if (isFixed) {
                        return (
                          <th
                            key={header.id}
                            style={style}
                            className="h-8 border-b border-border/60 bg-background p-0 text-left"
                          />
                        )
                      }

                      const inner = (
                        <>
                          <HeaderCellInner
                            label={label}
                            sortDirection={sortDirection}
                            onSortAsc={() => setPrefs((cur) => ({ ...cur, sorting: [{ id: header.column.id, desc: false }] }))}
                            onSortDesc={() => setPrefs((cur) => ({ ...cur, sorting: [{ id: header.column.id, desc: true }] }))}
                            canMoveLeft={!isPinned && visibleIndex > pinnedColumnIds.length}
                            canMoveRight={!isPinned && visibleIndex < visibleOrder.length - 1}
                            canHide={!isPinned}
                            onMoveLeft={() => moveColumn(header.column.id, -1)}
                            onMoveRight={() => moveColumn(header.column.id, 1)}
                            onHide={() => hideColumn(header.column.id)}
                          />
                          <ResizeHandle
                            onMouseDown={(e) => header.getResizeHandler()(e)}
                            onTouchStart={(e) => header.getResizeHandler()(e)}
                            isResizing={header.column.getIsResizing()}
                          />
                        </>
                      )

                      if (isPinned) {
                        return (
                          <PinnedHeaderCell key={header.id} style={style}>
                            {inner}
                          </PinnedHeaderCell>
                        )
                      }

                      return (
                        <SortableHeaderCell key={header.id} headerId={header.column.id} style={style}>
                          {inner}
                        </SortableHeaderCell>
                      )
                    })}
                  </SortableContext>

                  {/* "+ Add column" trailing header */}
                  <th
                    className="relative h-8 border-b border-border/60 bg-background p-0 text-left align-middle"
                    style={fixedStyle(trailingWidth)}
                  >
                    <div className="relative flex items-center px-3">
                      <button
                        ref={addBtnRef}
                        type="button"
                        onClick={() => setAddOpen((v) => !v)}
                        className="flex items-center gap-1.5 text-ui-value font-normal text-muted-foreground hover:text-foreground"
                      >
                        <HugeiconsIcon icon={Add01Icon} strokeWidth={2} className="size-3.5" />
                        Добавить
                      </button>
                      {addOpen && (
                        <AddColumnPanel
                          hiddenColumns={hiddenColumns}
                          onShow={showColumn}
                          onClose={() => setAddOpen(false)}
                          onCreateColumn={onCreateColumn}
                        />
                      )}
                    </div>
                  </th>
                </tr>
              ))}
            </thead>

            <tbody>
              {groups ? (
                // ── Grouped rendering ──
                (() => {
                  const allRows = table.getRowModel().rows
                  let rowIndex = 0
                  return groups.flatMap((group) => {
                    const groupRows = allRows.slice(rowIndex, rowIndex + group.rows.length)
                    rowIndex += group.rows.length
                    return [
                      <tr key={`${group.key}__header`}>
                        {visibleLeafColumns.map((col, colIndex) => {
                          const isPinned = pinnedSet.has(col.id)
                          return (
                            <td
                              key={col.id}
                              style={fixedStyle(col.getSize())}
                              className={cn(
                                "px-0 py-0",
                                isPinned && "sticky left-0 z-[2] bg-background",
                              )}
                            >
                              {colIndex === 0 && (
                                <div className="flex h-8 items-center px-3">{group.header}</div>
                              )}
                            </td>
                          )
                        })}
                        <td style={fixedStyle(trailingWidth)} />
                      </tr>,
                      rowDragEnabled ? (
                        <SortableContext
                          key={`${group.key}__rows`}
                          items={groupRows.map((row) => `${ROW_ID_PREFIX}${rowIdOf(row.original)}`)}
                          strategy={verticalListSortingStrategy}
                        >
                          {groupRows.map((row) => {
                            const rowId = rowIdOf(row.original)
                            return (
                              <SortableRow
                                key={row.id}
                                id={rowId}
                                isDraggingThis={activeRowId === rowId}
                              >
                                {renderRowCells(row)}
                                <td style={fixedStyle(trailingWidth)} />
                              </SortableRow>
                            )
                          })}
                        </SortableContext>
                      ) : (
                        groupRows.map((row) => (
                          <tr
                            key={row.id}
                            className="group transition-colors hover:bg-muted/30"
                          >
                            {renderRowCells(row)}
                            <td style={fixedStyle(trailingWidth)} />
                          </tr>
                        ))
                      ),
                    ]
                  })
                })()
              ) : rowDragEnabled ? (
                // ── Flat rendering with row drag ──
                <SortableContext items={sortableRowIds} strategy={verticalListSortingStrategy}>
                  {table.getRowModel().rows.map((row) => {
                    const rowId = rowIdOf(row.original)
                    return (
                      <SortableRow
                        key={row.id}
                        id={rowId}
                        isDraggingThis={activeRowId === rowId}
                      >
                        {renderRowCells(row)}
                        <td style={fixedStyle(trailingWidth)} />
                      </SortableRow>
                    )
                  })}
                </SortableContext>
              ) : (
                // ── Flat rendering without row drag ──
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="group transition-colors hover:bg-muted/30"
                  >
                    {renderRowCells(row)}
                    <td style={fixedStyle(trailingWidth)} />
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Ghost row following cursor during drag */}
          <DragOverlay dropAnimation={null}>
            {activeRow ? (
              <table
                className="border-separate border-spacing-0 text-ui-value font-normal"
                style={{ width: table.getTotalSize() + dragHandleOffset }}
              >
                <tbody>
                  <tr className="bg-background shadow-lg ring-1 ring-border rounded">
                    {renderRowCells(activeRow, true)}
                  </tr>
                </tbody>
              </table>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Footer */}
      {footer !== undefined && (
        <div className="shrink-0 border-t px-4 py-2 text-xs text-muted-foreground">
          {footer}
        </div>
      )}
    </div>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function enforcePinnedFirst(order: string[], pinnedIds: string[]): string[] {
  const pinnedSet = new Set(pinnedIds)
  return [...pinnedIds, ...order.filter((id) => !pinnedSet.has(id))]
}

// ─── Column cell wrapper helpers ──────────────────────────────────────────────

export function DataTableCell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("flex items-center overflow-hidden px-3 py-1 text-[12.5px] leading-none font-normal", className)}>
      {children}
    </div>
  )
}
